import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/infrastructure/query/queryClient';
import { useAuthStore } from '@/core/stores/useAuthStore';
import { ProtectedRoute } from '@/presentation/components/layout/ProtectedRoute';
import { ErrorBoundary } from '@/presentation/components/ErrorBoundary';
import { ConsoleLayout } from '@/presentation/components/layout/ConsoleLayout';
import { LoginScreen } from '@/screens/Login/LoginScreen';
import { ForgotPasswordScreen } from '@/screens/ForgotPassword/ForgotPasswordScreen';
import { Spinner } from '@/shared/ui';
import { STORE_RESOURCE_ROUTES } from '@/shared/resource-shell/storeRouteRegistry';

// Lazy load all page components for code splitting.
// Note: store-scoped ResourceShell routes (products/categories/...) live in
// STORE_RESOURCE_ROUTES and are wired below via `.map` so the
// `crab-console-internal/store-route-registry` eslint gate can statically
// verify every new store-scoped <Route>.
const TenantHomeResolver = lazy(() => import('@/screens/Dashboard/TenantHomeResolver').then(m => ({ default: m.TenantHomeResolver })));
const StoresScreen = lazy(() => import('@/screens/Stores/StoresScreen').then(m => ({ default: m.StoresScreen })));
const AuditScreen = lazy(() => import('@/screens/Audit/AuditScreen').then(m => ({ default: m.AuditScreen })));
const BillingScreen = lazy(() => import('@/screens/Billing/BillingScreen').then(m => ({ default: m.BillingScreen })));
const SettingsScreen = lazy(() => import('@/screens/Settings/SettingsScreen').then(m => ({ default: m.SettingsScreen })));
const StoreLayout = lazy(() => import('@/presentation/components/layout/StoreLayout').then(m => ({ default: m.StoreLayout })));
const StoreHomeScreen = lazy(() => import('@/screens/Store/Home/StoreHomeScreen').then(m => ({ default: m.StoreHomeScreen })));
const StoreSettingsScreen = lazy(() => import('@/screens/Store/Settings/StoreSettingsScreen').then(m => ({ default: m.StoreSettingsScreen })));
const LiveOrdersScreen = lazy(() => import('@/screens/Store/LiveOrders/LiveOrdersScreen').then(m => ({ default: m.LiveOrdersScreen })));
const OrdersScreen = lazy(() => import('@/screens/Store/Orders/OrdersScreen').then(m => ({ default: m.OrdersScreen })));
const AnalyticsShell = lazy(() => import('@/screens/Store/Analytics/AnalyticsShell').then(m => ({ default: m.AnalyticsShell })));
const PerformanceWorkflow = lazy(() => import('@/screens/Store/Analytics/PerformanceWorkflow').then(m => ({ default: m.PerformanceWorkflow })));
const SalesWorkflow = lazy(() => import('@/screens/Store/Analytics/SalesWorkflow').then(m => ({ default: m.SalesWorkflow })));
const ProductsWorkflow = lazy(() => import('@/screens/Store/Analytics/ProductsWorkflow').then(m => ({ default: m.ProductsWorkflow })));
const ProductDetailScreen = lazy(() => import('@/screens/Store/Analytics/ProductDetailScreen').then(m => ({ default: m.ProductDetailScreen })));
const CashWorkflow = lazy(() => import('@/screens/Store/Analytics/CashWorkflow').then(m => ({ default: m.CashWorkflow })));
const LossesWorkflow = lazy(() => import('@/screens/Store/Analytics/LossesWorkflow').then(m => ({ default: m.LossesWorkflow })));
const ReportsScreen = lazy(() => import('@/screens/Store/Reports/ReportsScreen').then(m => ({ default: m.ReportsScreen })));
const ReportDetailScreen = lazy(() => import('@/screens/Store/Reports/ReportDetailScreen').then(m => ({ default: m.ReportDetailScreen })));
const AnomaliesScreen = lazy(() => import('@/screens/Store/Anomalies/AnomaliesScreen').then(m => ({ default: m.AnomaliesScreen })));
const AnomalyDetailScreen = lazy(() => import('@/screens/Store/Anomalies/AnomalyDetailScreen').then(m => ({ default: m.AnomalyDetailScreen })));
const DataTransferScreen = lazy(() => import('@/screens/Store/DataTransfer/DataTransferScreen').then(m => ({ default: m.DataTransferScreen })));
const ShiftsScreen = lazy(() => import('@/screens/Store/Shifts/ShiftsScreen').then(m => ({ default: m.ShiftsScreen })));
const ComplianceScreen = lazy(() => import('@/compliance/ComplianceScreen').then(m => ({ default: m.ComplianceScreen })));
const KitchenScreen = lazy(() => import('@/screens/Store/Kitchen/KitchenScreen').then(m => ({ default: m.KitchenScreen })));
const ReservationsView = lazy(() => import('@/screens/Store/Reservations/ReservationsView').then(m => ({ default: m.ReservationsView })));
const ReservationSettingsView = lazy(() => import('@/screens/Store/Reservations/ReservationSettings').then(m => ({ default: m.ReservationSettingsView })));
const PlatformIntegrationScreen = lazy(() => import('@/screens/Store/Settings/PlatformIntegration').then(m => ({ default: m.PlatformIntegration })));
const UberEatsOAuthRedirect = lazy(() => import('@/screens/Store/Settings/UberEatsOAuthRedirect').then(m => ({ default: m.UberEatsOAuthRedirect })));
const FiscalSetupScreen = lazy(() => import('@/screens/FiscalSetup/FiscalSetupScreen').then(m => ({ default: m.FiscalSetupScreen })));

const PageLoading: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Spinner size="lg" className="text-primary-500" />
  </div>
);

/**
 * Drop all cached queries when the tenant identity changes — both sign-out
 * (tenantId → null) AND account switch (tenantId → new). `switchAccount` in
 * useAuthStore doesn't flip `isAuthenticated`, so watching that alone left
 * the previous tenant's queries + in-flight raw fetches writing into the
 * new tenant's views. Watching `tenantId` covers both transitions.
 *
 * Routes below also use `key={tenantId ?? 'anon'}` so the entire route tree
 * remounts on tenant change — that kills any stale closure in useEffect
 * callbacks (snapshot tokens, in-flight fetch chains) that React Query's
 * cancelQueries doesn't reach.
 */
function useClearQueriesOnTenantChange() {
  const tenantId = useAuthStore(s => s.tenantId);
  const prevTenantId = useRef(tenantId);
  useEffect(() => {
    if (prevTenantId.current !== tenantId) {
      queryClient.cancelQueries();
      queryClient.clear();
    }
    prevTenantId.current = tenantId;
  }, [tenantId]);
}

const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useClearQueriesOnTenantChange();
  return <>{children}</>;
};

const AnalyticsIndexRedirect: React.FC = () => {
  const location = useLocation();
  return <Navigate to={`performance${location.search}`} replace />;
};

export const App: React.FC = () => {
  const tenantId = useAuthStore(s => s.tenantId);
  return (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <AppShell>
    <Suspense fallback={<PageLoading />}>
      <Routes key={tenantId ?? 'anon'}>
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/forgot-password" element={<ForgotPasswordScreen />} />

        {/* Full-screen production fiscal identity gate — sits OUTSIDE both
            ConsoleLayout and StoreLayout so it covers the navigation and is not
            recursively gated by either layout (spec §9.2 / Task 3 Step 3). */}
        <Route path="/fiscal-setup" element={<ProtectedRoute><FiscalSetupScreen /></ProtectedRoute>} />

        <Route element={<ProtectedRoute><ConsoleLayout /></ProtectedRoute>}>
          <Route path="/" element={<TenantHomeResolver />} />
          <Route path="/stores" element={<StoresScreen />} />
          <Route path="/audit" element={<AuditScreen />} />
          <Route path="/billing" element={<BillingScreen />} />
          <Route path="/settings" element={<SettingsScreen />} />
        </Route>

        <Route path="/stores/:id" element={<ProtectedRoute><StoreLayout /></ProtectedRoute>}>
          <Route index element={<StoreHomeScreen />} />
          <Route path="analytics" element={<AnalyticsShell />}>
            <Route index element={<AnalyticsIndexRedirect />} />
            <Route path="performance" element={<PerformanceWorkflow />} />
            <Route path="sales" element={<SalesWorkflow />} />
            <Route path="products" element={<ProductsWorkflow />} />
            <Route path="products/:productId" element={<ProductDetailScreen />} />
            <Route path="cash" element={<CashWorkflow />} />
            <Route path="losses" element={<LossesWorkflow />} />
          </Route>
          <Route path="settings" element={<StoreSettingsScreen />} />
          <Route path="live" element={<LiveOrdersScreen />} />
          <Route path="orders" element={<OrdersScreen />} />
          <Route path="reports" element={<ReportsScreen />} />
          <Route path="reports/daily/:date" element={<ReportDetailScreen />} />
          <Route path="reports/weekly/:weekStart" element={<ReportDetailScreen />} />
          <Route path="reports/monthly/:month" element={<ReportDetailScreen />} />
          {STORE_RESOURCE_ROUTES.map((r) => (
            <Route key={r.routePath} path={r.routePath} element={<r.Component />} />
          ))}
          <Route path="data-transfer" element={<DataTransferScreen />} />
          <Route path="shifts" element={<ShiftsScreen />} />
          <Route path="compliance" element={<ComplianceScreen />} />
          <Route path="kitchen" element={<KitchenScreen />} />
          <Route path="reservations" element={<ReservationsView />} />
          <Route path="reservation-settings" element={<ReservationSettingsView />} />
          <Route path="platforms" element={<PlatformIntegrationScreen />} />
          <Route path="anomalies" element={<AnomaliesScreen />} />
          <Route path="anomalies/:anomalyId" element={<AnomalyDetailScreen />} />
        </Route>

        {/* Uber Eats OAuth redirect handler */}
        <Route path="/oauth/uber-eats" element={<ProtectedRoute><UberEatsOAuthRedirect /></ProtectedRoute>} />

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Suspense>
    </AppShell>
  </QueryClientProvider>
  </ErrorBoundary>
  );
};
